import { Menu, X } from 'lucide-react';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { useFoundersPromoCount } from '../../hooks/useFoundersPromoCount';
import { getFoundersPromoCompactDisplay } from '../../utils/foundersPromoDisplay';
import { FoundersPromoBadge } from '../ui/FoundersPromoBadge';

export const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { data: promoCount = 0, isLoading: isLoadingCount } = useFoundersPromoCount();
  const compactDisplay = getFoundersPromoCompactDisplay(promoCount);
  const remainingSpots = Math.max(0, 100 - promoCount);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 flex h-16 items-center justify-between overflow-hidden px-3 font-['Inter'] sm:px-4 lg:px-6 bg-white/[0.08] backdrop-blur-3xl backdrop-saturate-[1.8] border-b border-white/20 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.12)]">
        <a href="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity flex-shrink-0">
          <img src="/rollops-logo.png" alt="RollOps Logo" width={32} height={32} className="w-8 h-8 sm:w-10 sm:h-10" />
          <span className="text-xl sm:text-2xl font-bold text-[#EBFFEE]">
            Roll<span className="text-rollops-accent">O</span>ps
          </span>
        </a>
        
        <div className="flex items-center gap-1 sm:gap-1.5 md:gap-2 lg:gap-3 flex-shrink-0 min-w-0">
          {/* Sign Up Button with Founders Promo - Combined */}
          <FoundersPromoBadge
            displayText={compactDisplay}
            isLoading={isLoadingCount}
            remainingSpots={remainingSpots}
            variant="compact"
          />
          
          {/* Desktop Navigation - Show at md (768px+) but ensure Login is always visible */}
          <nav className="hidden md:flex items-center space-x-3 lg:space-x-4 xl:space-x-6 2xl:space-x-8 flex-shrink-0">
            <a href="/#features" className="text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors whitespace-nowrap text-xs md:text-sm lg:text-base">
              Features
            </a>
            <a href="/#about" className="text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors whitespace-nowrap text-xs md:text-sm lg:text-base hidden lg:inline">
              About Us
            </a>
            <a href="/#contact" className="text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors whitespace-nowrap text-xs md:text-sm lg:text-base hidden xl:inline">
              Contact Us
            </a>
            <Link 
              to="/signin" 
              className="text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors whitespace-nowrap text-xs md:text-sm lg:text-base flex-shrink-0 ml-1"
            >
              Login
            </Link>
          </nav>

          {/* Mobile Menu Button - Contains Sign In - Show below md (768px) */}
          <button
            className="md:hidden text-[#EBFFEE] p-2 flex-shrink-0"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden fixed inset-x-0 top-16 bottom-0 z-40 bg-[#1a1818] border-t border-white/10 overflow-y-auto">
          <nav className="px-4 py-6 space-y-4">
            <a 
              href="/#features" 
              className="block text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors py-2"
              onClick={() => setIsMenuOpen(false)}
            >
              Features
            </a>
            <a 
              href="/#about" 
              className="block text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors py-2"
              onClick={() => setIsMenuOpen(false)}
            >
              About Us
            </a>
            <a 
              href="/#contact" 
              className="block text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors py-2"
              onClick={() => setIsMenuOpen(false)}
            >
              Contact Us
            </a>
            {/* Mobile Founders Promo */}
            <div className="pt-4 border-t border-white/10">
              <FoundersPromoBadge
                displayText={compactDisplay}
                isLoading={isLoadingCount}
                remainingSpots={remainingSpots}
                variant="card"
              />
            </div>
            <div className="pt-4 space-y-3 border-t border-white/10">
              <Link 
                to="/signin" 
                className="block text-center bg-[#EBFFEE] hover:bg-[#A9A3A3] text-[#111010] px-4 py-3 rounded-md font-medium transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Sign In
              </Link>
              <Link 
                to="/owner-signup" 
                className="block text-center bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] px-4 py-3 rounded-md font-semibold transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Claim Spot
              </Link>
            </div>
          </nav>
        </div>
      )}
    </>
  );
}; 